/**
 * Configuração › Autorizações de marca (cliente). Marcas, fabricantes ou EANs com venda restrita pelo detentor que
 * pegam itens desta conta: os itens e anúncios ativos atingidos, a situação da autorização e o envio do documento do
 * detentor (PDF, JPG ou PNG até 10 MB), que a equipe Berytus analisa. Enquanto a regra está em observe nada é travado;
 * quando entrar a trava, sem autorização aprovada o item não publica e os anúncios ativos são pausados.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, dateTime, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';

interface Item { id: string; label: string; value: string; holder: string | null; reason: string; active: boolean; products: number; activeListings: number; authorized: boolean;
  examples: { id: string; sku: string; title: string; activeListings: number }[];
  authorizations: { id: string; status: string; validUntil: string | null; note: string | null; fileName: string; rejectReason: string | null; createdAt: string }[] }
const ST: Record<string, [string, string]> = { pending_review: ['em análise', 'info'], approved: ['aprovada', 'ok'], rejected: ['recusada', 'err'], revoked: ['revogada', 'off'] };
const br = (d: string | null) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : 'sem prazo');
const toBase64 = (f: File) => new Promise<string>((ok, bad) => { const r = new FileReader(); r.onload = () => ok(String(r.result).split(',')[1] ?? ''); r.onerror = bad; r.readAsDataURL(f); });

export function BrandAuthorizations() {
  const q = useQuery({ queryKey: ['brand-restrictions'], queryFn: () => api<{ ruleMode: string; items: Item[] }>('/brand-restrictions') });
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Autorizações de marca</h1>
          <p>Alguns detentores (marcas e fabricantes) só permitem a venda por lojas autorizadas. Para vender esses itens nos canais, envie a autorização do detentor; a equipe Berytus confere e libera.</p>
        </div>
      </div>
      {q.error ? <ErrorBox error={q.error} /> : !q.data ? <Loading /> : (
        <div className="stack-y">
          {q.data.ruleMode !== 'enforce' && <div className="notice small">Por enquanto nada é travado: os itens abaixo continuam publicando. Quando a regra entrar em vigor, sem a autorização aprovada o item não publica e os anúncios ativos são pausados.</div>}
          {!q.data.items.length ? <div className="empty"><b>Nenhuma restrição</b>Nenhum item desta conta é de marca com venda restrita.</div> : q.data.items.map((r) => <Restriction key={r.id} r={r} />)}
        </div>
      )}
    </>
  );
}

function Restriction({ r }: { r: Item }) {
  const qc = useQueryClient();
  const [validUntil, setValidUntil] = useState('');
  const [note, setNote] = useState('');
  const send = useMutation({
    mutationFn: async (f: File) => api('/brand-restrictions/' + r.id + '/authorizations', { method: 'POST', json: { fileName: f.name, base64: await toBase64(f), validUntil: validUntil || undefined, note: note || undefined } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['brand-restrictions'] }),
  });
  const last = r.authorizations[0];
  const pending = last?.status === 'pending_review';
  return (
    <section className="panel stack-y" style={{ gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, flex: 1 }}>{r.label}: {r.value}{r.holder ? <span className="muted small"> · detentor {r.holder}</span> : null}</h2>
        {!r.active ? <Tag tone="off">restrição encerrada</Tag> : r.authorized ? <Tag tone="ok">autorizada</Tag> : pending ? <Tag tone="info">autorização em análise</Tag> : <Tag tone="warn">sem autorização</Tag>}
      </div>
      <div className="small muted">{r.reason} · {int(r.products)} item(ns) desta conta · {int(r.activeListings)} anúncio(s) ativo(s)</div>
      {r.examples.length > 0 && <div className="small">{r.examples.map((x) => `${x.sku} ${x.title}`).join(' · ')}{r.products > r.examples.length ? ` · e mais ${int(r.products - r.examples.length)}` : ''}</div>}
      {r.active && !r.authorized && !pending && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'end' }}>
          <label className="small" style={{ display: 'grid', gap: 4 }}>Validade da autorização (se houver)<input className="input" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} /></label>
          <label className="small" style={{ display: 'grid', gap: 4, flex: '1 1 220px' }}>Observação<input className="input" value={note} onChange={(e) => setNote(e.target.value)} /></label>
          <label className="small" style={{ display: 'grid', gap: 4 }}>Autorização do detentor (PDF, JPG ou PNG)
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" disabled={send.isPending} onChange={(e) => { const f = e.target.files?.[0]; if (f) send.mutate(f); e.target.value = ''; }} /></label>
        </div>
      )}
      {send.error && <ErrorBox error={send.error} />}
      {r.authorizations.length > 0 && (
        <table className="simple">
          <thead><tr><th>Enviada</th><th>Arquivo</th><th>Validade</th><th>Situação</th></tr></thead>
          <tbody>{r.authorizations.map((a) => (
            <tr key={a.id}>
              <td className="small">{dateTime(a.createdAt)}</td>
              <td className="small"><a href={`/api/v1/brand-authorizations/${a.id}/file`} target="_blank" rel="noreferrer">{a.fileName}</a>{a.note ? <div className="muted">{a.note}</div> : null}</td>
              <td>{br(a.validUntil)}</td>
              <td><Tag tone={ST[a.status]?.[1] ?? 'off'}>{ST[a.status]?.[0] ?? a.status}</Tag>{a.rejectReason && <div className="small" style={{ color: 'var(--err)' }}>{a.rejectReason}</div>}</td>
            </tr>))}</tbody>
        </table>
      )}
    </section>
  );
}
